import type { GetterTree } from 'vuex'
import type {
  DeviceModelField,
  DeviceSubModel,
  DeviceFieldCalibration,
  DeviceFieldAllowedRange,
  DevicePins
} from '@/types/devices'
import type { RootState } from '@/store'
import type { DeviceModelsState } from './types'
import { DEVICE_MODELS_GETTERS } from './types'
import { measurementShortNameToType } from '@/services/device-measurements'
import type { UnpackedTelemetryField } from '@/services/device-service/devices'

const alertMeasurementsSortOrder: Record<string, number> = {
  pm25_raw: 0,
  pm10_raw: 1,
  pm25: 2,
  pm10: 3,
  temp: 4,
  rh: 5,
  baro_inhg: 6,
  co: 7,
  co2: 8,
  tvoc: 9,
  ch2o: 10,
  so2: 11,
  o3: 12,
  no2: 13
}

const sortAlertMeasurements = (
  [, a]: [string, DeviceModelField],
  [, b]: [string, DeviceModelField]
): number => {
  return alertMeasurementsSortOrder[a.shortName] > alertMeasurementsSortOrder[b.shortName] ? 1 : -1
}

interface IDeviceModelsGetters extends GetterTree<DeviceModelsState, RootState> {
  [DEVICE_MODELS_GETTERS.GET_ALLOW_PUBLIC](state: DeviceModelsState): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_AQI](state: DeviceModelsState): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_CONDITION](state: DeviceModelsState): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_CONNECTIVITY](
    state: DeviceModelsState
  ): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_LAST_COMMUNICATION](
    state: DeviceModelsState
  ): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS](
    state: DeviceModelsState
  ): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_REGISTRATION](
    state: DeviceModelsState
  ): (model: string) => boolean | undefined
  [DEVICE_MODELS_GETTERS.GET_SHOW_SUBSCRIPTION](
    state: DeviceModelsState
  ): (model: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_TELEMETRY_FIELDS_BY_MODEL](
    state: DeviceModelsState
  ): (model: string) => UnpackedTelemetryField | undefined
  [DEVICE_MODELS_GETTERS.GET_MODELS](state: DeviceModelsState): string[]
  [DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS_MODELS](state: DeviceModelsState): string[]
  [DEVICE_MODELS_GETTERS.GET_SUBMODELS](
    state: DeviceModelsState
  ): Record<string, DeviceSubModel[] | null | undefined>
  [DEVICE_MODELS_GETTERS.GET_MODEL_NAME](
    state: DeviceModelsState
  ): (modelNumber: string) => string | undefined
  [DEVICE_MODELS_GETTERS.GET_MODEL_NAMES_MAP](state: DeviceModelsState): Record<string, string>
  [DEVICE_MODELS_GETTERS.GET_MODEL_PRODUCT_FAMILY](
    state: DeviceModelsState
  ): (modelNumber: string) => string | null
  [DEVICE_MODELS_GETTERS.GET_MODEL_KEY](
    state: DeviceModelsState
  ): (modelNumber: string) => string | null
  [DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY](
    state: DeviceModelsState
  ): (modelNumber: string, submodelName: string) => string
  [DEVICE_MODELS_GETTERS.GET_SERIAL_LENGTH](
    state: DeviceModelsState
  ): (modelNumber: string) => number | undefined
  [DEVICE_MODELS_GETTERS.GET_INDOOR_ONLY](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_CALIBRATION](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_INDOOR_SENSOR](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_INTERVAL_SETTINGS](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_SHOW_LOCATION](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_FAQ_LINK](
    state: DeviceModelsState
  ): (modelNumber: string) => string | undefined
  [DEVICE_MODELS_GETTERS.GET_CALIBRATION_LIMITS](
    state: DeviceModelsState
  ): (
    modelNumber: string,
    submodelName: string | null
  ) => Record<string, DeviceFieldCalibration | undefined>
  [DEVICE_MODELS_GETTERS.GET_ALLOWED_RANGE_LIMITS](
    state: DeviceModelsState
  ): (
    modelNumber: string,
    submodelName: string | null
  ) => Record<string, DeviceFieldAllowedRange | undefined>
  [DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS](
    state: DeviceModelsState
  ): (modelNumber: string, submodelName: string | null, publicOnly: boolean) => number[]
  [DEVICE_MODELS_GETTERS.GET_ALERTABLE_MEASUREMENTS](
    state: DeviceModelsState
  ): (modelNumber: string, submodelName: string | null) => string[]
  [DEVICE_MODELS_GETTERS.GET_ZEROABLE_MEASUREMENTS](
    state: DeviceModelsState
  ): (modelNumber: string, submodelName: string | null) => string[]
  [DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_ZEROING](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_AUTO_CAL](
    state: DeviceModelsState
  ): (modelNumber: string) => boolean
  [DEVICE_MODELS_GETTERS.GET_PIN_PATH](
    state: DeviceModelsState
  ): (modelNumber: string, type: keyof DevicePins) => string
  [DEVICE_MODELS_GETTERS.GET_STATE_FIELDS](
    state: DeviceModelsState
  ): (modelNumber: string) => DeviceModelField[]
}

const getters: IDeviceModelsGetters = {
  [DEVICE_MODELS_GETTERS.GET_ALLOW_PUBLIC](state) {
    return (model) => !!state.modelsMetaData[model]?.allowPublic
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_AQI](state) {
    return (model) => !!state.modelsMetaData[model]?.showAQI
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_CONDITION](state) {
    return (model) => !!state.modelsMetaData[model]?.showCondition
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_CONNECTIVITY](state) {
    return (model) => !!state.modelsMetaData[model]?.showConnectivity
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_LAST_COMMUNICATION](state) {
    return (model) => !!state.modelsMetaData[model]?.showLastCommunication
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS](state) {
    return (model) => !!state.modelsMetaData[model]?.showMapSettings
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_REGISTRATION](state) {
    return (model) => state.modelsMetaData[model]?.showRegistration
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_SUBSCRIPTION](state) {
    return (model) => !!state.modelsMetaData[model]?.showSubscription
  },
  [DEVICE_MODELS_GETTERS.GET_TELEMETRY_FIELDS_BY_MODEL](state) {
    return (model) => state.modelsMetaData[model]?.telemetryFields
  },
  [DEVICE_MODELS_GETTERS.GET_MODELS](state) {
    return Object.keys(state.modelsMetaData)
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_MAP_SETTINGS_MODELS](state) {
    const models = Object.keys(state.modelsMetaData)
    if (models?.length) {
      return models.filter((model) => !!state.modelsMetaData[model]?.showMapSettings)
    }
    return []
  },
  [DEVICE_MODELS_GETTERS.GET_SUBMODELS](state) {
    const submodels: Record<string, DeviceSubModel[] | null | undefined> = {}
    for (const model in state.modelsMetaData) {
      submodels[model] = state.modelsMetaData[model]?.subModels
    }
    return submodels
  },
  [DEVICE_MODELS_GETTERS.GET_MODEL_NAME](state) {
    return (modelNumber) => state.modelsMetaData[modelNumber]?.name
  },
  [DEVICE_MODELS_GETTERS.GET_MODEL_NAMES_MAP](state) {
    const modelNamesMap: Record<string, string> = {}
    Object.entries(state.modelsMetaData).forEach(([modelNumber, model]) => {
      modelNamesMap[modelNumber] = model.name
    })
    return modelNamesMap
  },
  [DEVICE_MODELS_GETTERS.GET_MODEL_PRODUCT_FAMILY](state) {
    return (modelNumber) => {
      if (modelNumber && state.modelsMetaData[modelNumber]) {
        return state.modelsMetaData[modelNumber].productFamily
      }
      return null
    }
  },
  [DEVICE_MODELS_GETTERS.GET_MODEL_KEY](state) {
    return (modelNumber) => {
      if (modelNumber && state.modelsMetaData[modelNumber]) {
        return state.modelsMetaData[modelNumber].key
      }
      return null
    }
  },
  [DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY](state) {
    return (modelNumber, submodelName) => {
      const model = state.modelsMetaData[modelNumber]
      if (model?.subModels && submodelName) {
        return state.modelsMetaData[modelNumber].subModels.find((sm) => sm.name === submodelName)
          .key
      }
      return ''
    }
  },
  [DEVICE_MODELS_GETTERS.GET_SERIAL_LENGTH](state) {
    return (modelNumber) => state.modelsMetaData[modelNumber]?.serialLength
  },
  [DEVICE_MODELS_GETTERS.GET_INDOOR_ONLY](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.indoorOnly
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_CALIBRATION](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.showCalibration
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_INDOOR_SENSOR](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.showIndoorSensor
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_INTERVAL_SETTINGS](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.showIntervalSettings
  },
  [DEVICE_MODELS_GETTERS.GET_SHOW_LOCATION](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.showLocation
  },
  [DEVICE_MODELS_GETTERS.GET_FAQ_LINK](state) {
    return (modelNumber) => state.modelsMetaData[modelNumber]?.faqLink
  },
  [DEVICE_MODELS_GETTERS.GET_CALIBRATION_LIMITS](state) {
    return (modelNumber, submodelName) => {
      const calibrationLimits: Record<string, DeviceFieldCalibration | undefined> = {}
      const model = state.modelsMetaData[modelNumber]
      const telemetryFields = model.telemetryFields

      let calFields = Object.entries(telemetryFields).filter(([, tf]) => tf.calibration)
      if (model.subModels && submodelName) {
        const submodel = model.subModels.find((sm) => sm.name === submodelName)
        calFields = calFields.filter(([, tf]) => tf.subModel.includes(submodel.shortName))
      }
      calFields.forEach(([, tf]) => {
        calibrationLimits[tf.shortName] = tf.calibration
      })
      return calibrationLimits
    }
  },
  [DEVICE_MODELS_GETTERS.GET_ALLOWED_RANGE_LIMITS](state) {
    return (modelNumber, submodelName) => {
      const allowedRangeLimits: Record<string, DeviceFieldAllowedRange | undefined> = {}
      const model = state.modelsMetaData[modelNumber]
      const telemetryFields = model.telemetryFields

      let fields = Object.entries(telemetryFields).filter(([, tf]) => tf.allowedRange)
      if (model.subModels && submodelName) {
        const submodel = model.subModels.find((sm) => sm.name === submodelName)
        fields = fields.filter(([, tf]) => tf.subModel.includes(submodel.shortName))
      }
      fields.forEach(([, tf]) => {
        allowedRangeLimits[tf.shortName] = tf.allowedRange
      })
      return allowedRangeLimits
    }
  },
  [DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS](state) {
    return (modelNumber, submodelName, publicOnly) => {
      const model = state.modelsMetaData[modelNumber]

      if (model) {
        const telemetryFields = model.telemetryFields
        let supportedTelemetryFields = model.telemetryFields
          ? Object.entries(telemetryFields).filter(([, tf]) => tf.supportedInUI)
          : []
        if (publicOnly) {
          supportedTelemetryFields = supportedTelemetryFields.filter(
            ([, tf]) => tf.availableToPublic
          )
        }
        if (model.subModels && submodelName) {
          const submodel = model.subModels.find((sm) => sm.name === submodelName)
          supportedTelemetryFields = supportedTelemetryFields.filter(([, tf]) =>
            tf.subModel.includes(submodel.shortName)
          )
        }
        return supportedTelemetryFields
          .map(([, tf]) => measurementShortNameToType[tf.shortName])
          .filter((m): m is number => typeof m === 'number')
      }

      return []
    }
  },
  [DEVICE_MODELS_GETTERS.GET_ALERTABLE_MEASUREMENTS](state) {
    return (modelNumber, submodelName) => {
      const model = state.modelsMetaData[modelNumber]

      if (model?.telemetryFields) {
        const telemetryFields = model.telemetryFields
        let supportedTelemetryFields = Object.entries(telemetryFields).filter(
          ([, tf]) => tf.alertable
        )
        if (model.subModels && submodelName) {
          const submodel = model.subModels.find((sm) => sm.name === submodelName)
          supportedTelemetryFields = supportedTelemetryFields.filter(([, tf]) =>
            tf.subModel.includes(submodel?.shortName)
          )
        }
        supportedTelemetryFields.sort(sortAlertMeasurements)
        return supportedTelemetryFields.map(([tfName]) => tfName)
      }

      return []
    }
  },
  [DEVICE_MODELS_GETTERS.GET_ZEROABLE_MEASUREMENTS](state) {
    return (modelNumber, submodelName) => {
      const zeroableMeasurements: string[] = []
      const model = state.modelsMetaData[modelNumber]
      const telemetryFields = model.telemetryFields

      let supportedTelemetryFields = Object.entries(telemetryFields).filter(([, tf]) => tf.zeroable)
      if (model.subModels && submodelName) {
        const submodel = model.subModels.find((sm) => sm.name === submodelName)
        supportedTelemetryFields = supportedTelemetryFields.filter(([, tf]) =>
          tf.subModel.includes(submodel?.shortName)
        )
      }
      supportedTelemetryFields.forEach(([, tf]) => {
        zeroableMeasurements.push(tf.shortName)
      })
      return zeroableMeasurements
    }
  },
  [DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_ZEROING](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.zeroable
  },
  [DEVICE_MODELS_GETTERS.MODEL_SUPPORTS_AUTO_CAL](state) {
    return (modelNumber) => !!state.modelsMetaData[modelNumber]?.supportsAutoCal
  },
  [DEVICE_MODELS_GETTERS.GET_PIN_PATH](state) {
    return (modelNumber: string, type: keyof DevicePins) => {
      const imgSettings = state.modelsMetaData[modelNumber]?.imgSettings
      const pins = imgSettings?.map?.pins
      const value = pins?.[type]
      if (value) {
        return `img/map/pins/${value}`
      }
      const defaultCase = type === 'indoor' ? 'pin-blank-indoor.svg' : 'pin-blank-outdoor.svg'
      return `img/map/pins/${defaultCase}`
    }
  },
  [DEVICE_MODELS_GETTERS.GET_STATE_FIELDS](state) {
    return (modelNumber) => state.modelsMetaData[modelNumber]?.stateFields || []
  }
}

export default getters
